import { useState } from "react";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StarInput } from "@/components/StarRating";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function ReviewDialog({ trip, counterpartyLabel, onClose, onDone }) {
  const { t } = useTranslation();
  const label = counterpartyLabel || t("review.theOtherParty");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!rating) { toast.error(t("review.chooseRating")); return; }
    setSaving(true);
    try {
      await api.post("/reviews", { booking_id: trip.booking_id, rating, comment });
      toast.success(t("review.submittedToast"));
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="review-dialog" className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">{t("review.title")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-500">{trip.car_name} · {label}</p>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">{t("review.yourRating")}</p>
            <StarInput value={rating} onChange={setRating} />
          </div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" rows={4}
            className="belo-input w-full resize-none" placeholder={t("review.commentPlaceholder")} />
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex gap-3 items-start text-sm text-slate-600">
            <Info size={18} className="text-blue-600 shrink-0 mt-0.5" />
            <span>{t("review.doubleBlind", { party: label })}</span>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} data-testid="review-cancel" className="rounded-full bg-white border border-slate-200 text-slate-700 px-5 py-2.5 font-semibold text-sm hover:bg-slate-50">{t("review.cancel")}</button>
            <button onClick={submit} disabled={saving} data-testid="review-modal-submit-button" className="rounded-full bg-blue-600 text-white px-6 py-2.5 font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">{t("review.submit")}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
